import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { Plus, Search } from "lucide-react";
import { SortingState } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { userAtom } from "@/stores/authStore";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import usePagination from "@/hooks/useSyncedPagination";
import { useDebounce } from "@/hooks/useDebounce";
import { useGetAllProducts, useDeleteProduct } from "@/api/product.api";
import { useGetAllProductsUsa, useDeleteProductUsa } from "@/api/productUsa.api";
import { useGetAllImporters } from "@/api/importer.api";
import { useGetCompanyBoListV1 } from "@/api/company.api";
import { ProductDto, ProductUSA } from "@/types/api/api-types";
import { getProductColumns } from "@/components/backOffice/products/productTableColumns";
import { getProductUsaColumns } from "@/components/backOffice/products/productUsaTableColumns";
import { ProductFormModal } from "@/components/backOffice/products/ProductFormModal";
import { ProductUsaFormModal } from "@/components/backOffice/products/ProductUsaFormModal";
import { ProductDeleteModal } from "@/components/backOffice/products/ProductDeleteModal";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { useWineColorLabels } from "@/hooks/useEnumLabels";
import { useWineColorOptions } from "@/hooks/useEnumOptions";
import { useTranslation } from "react-i18next";

type Tab = "product" | "productUsa";

const TABS: Tab[] = ["product", "productUsa"];

export const ProductsPage = () => {
  const { t } = useTranslation("boCatalog");
  const tabLabels: Record<Tab, string> = {
    product: t("productsPage.tabs.product"),
    productUsa: t("productsPage.tabs.productUsa"),
  };
  const wineColorOptions = useWineColorOptions();
  const wineColorLabels = useWineColorLabels();
  const user = useAtomValue(userAtom);

  const [activeTab, setActiveTab] = useState<Tab>("product");

  // Product state
  const [productSorting, setProductSorting] = useState<SortingState>([
    { id: "description", desc: false },
  ]);
  const [productPagination, setProductPagination] = usePagination("products");
  const [productSearch, setProductSearch] = useState("");
  const [productColor, setProductColor] = useState<WineColorEnum | "">("");
  const debouncedProductSearch = useDebounce(productSearch, 300);

  const { data: products } = useGetAllProducts({
    search: debouncedProductSearch || undefined,
    color: productColor || undefined,
    take: productPagination.pageSize,
    skip: productPagination.pageIndex * productPagination.pageSize,
    sortBy: productSorting[0]?.id,
    sortOrder: productSorting[0]?.desc ? "desc" : "asc",
  });
  const { mutateAsync: deleteProduct, isPending: isDeletingProduct } = useDeleteProduct();
  const [productFormOpen, setProductFormOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<ProductDto | null>(null);
  const [deleteProductModal, setDeleteProductModal] = useState<ProductDto | null>(null);

  // Product USA state
  const [usaSorting, setUsaSorting] = useState<SortingState>([{ id: "description", desc: false }]);
  const [usaPagination, setUsaPagination] = usePagination("productsUsa");
  const [usaSearch, setUsaSearch] = useState("");
  const [usaColor, setUsaColor] = useState<WineColorEnum | "">("");
  const [usaImporterId, setUsaImporterId] = useState("");
  const [usaClientId, setUsaClientId] = useState("");
  const debouncedUsaSearch = useDebounce(usaSearch, 300);

  const { data: productsUsa } = useGetAllProductsUsa({
    search: debouncedUsaSearch || undefined,
    color: usaColor || undefined,
    importerId: usaImporterId || undefined,
    clientId: usaClientId || undefined,
    take: usaPagination.pageSize,
    skip: usaPagination.pageIndex * usaPagination.pageSize,
    sortBy: usaSorting[0]?.id,
    sortOrder: usaSorting[0]?.desc ? "desc" : "asc",
  });
  const { mutateAsync: deleteProductUsa, isPending: isDeletingProductUsa } = useDeleteProductUsa();
  const [productUsaFormOpen, setProductUsaFormOpen] = useState(false);
  const [editProductUsa, setEditProductUsa] = useState<ProductUSA | null>(null);
  const [deleteProductUsaModal, setDeleteProductUsaModal] = useState<ProductUSA | null>(null);

  const { data: importers } = useGetAllImporters();
  const { data: companies } = useGetCompanyBoListV1();

  const importerOptions = useMemo(
    () => (importers?.data || []).map((i) => ({ value: i.id, label: i.name })),
    [importers],
  );
  const clientOptions = useMemo(
    () => (companies?.data || []).map((c) => ({ value: c.id, label: c.businessName })),
    [companies],
  );

  useEffect(() => {
    setProductPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [debouncedProductSearch, productColor, setProductPagination]);

  useEffect(() => {
    setUsaPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [debouncedUsaSearch, usaColor, usaImporterId, usaClientId, setUsaPagination]);

  // Product handlers
  const handleCreateProduct = () => {
    setEditProduct(null);
    setProductFormOpen(true);
  };

  const handleEditProduct = (product: ProductDto) => {
    setEditProduct(product);
    setProductFormOpen(true);
  };

  const handleCloseProductForm = () => {
    setProductFormOpen(false);
    setEditProduct(null);
  };

  const handleDeleteProduct = (product: ProductDto) => {
    setDeleteProductModal(product);
  };

  const handleCloseDeleteProduct = () => {
    setDeleteProductModal(null);
  };

  // Product USA handlers
  const handleCreateProductUsa = () => {
    setEditProductUsa(null);
    setProductUsaFormOpen(true);
  };

  const handleEditProductUsa = (product: ProductUSA) => {
    setEditProductUsa(product);
    setProductUsaFormOpen(true);
  };

  const handleCloseProductUsaForm = () => {
    setProductUsaFormOpen(false);
    setEditProductUsa(null);
  };

  const handleDeleteProductUsa = (product: ProductUSA) => {
    setDeleteProductUsaModal(product);
  };

  const handleCloseDeleteProductUsa = () => {
    setDeleteProductUsaModal(null);
  };

  const canManageProducts = useMemo(() => {
    return user?.role === "SUPERADMIN" || user?.role === "ADMIN" || user?.role === "MEMBER";
  }, [user]);

  const productColumns = useMemo(
    () => getProductColumns(t, handleEditProduct, handleDeleteProduct, wineColorLabels),
    [t, wineColorLabels],
  );
  const productUsaColumns = useMemo(
    () =>
      getProductUsaColumns(
        t,
        handleEditProductUsa,
        handleDeleteProductUsa,
        canManageProducts,
        wineColorLabels,
      ),
    [t, canManageProducts, wineColorLabels],
  );

  return (
    <div className="flex h-fit w-full flex-1 flex-col gap-4 p-8">
      <div className="flex w-full flex-row items-center justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="font-jakarta-sans text-3xl font-semibold">{t("productsPage.title")}</h1>
          <p>{t("productsPage.subtitle")}</p>
        </div>
        {canManageProducts && (
          <Button onClick={activeTab === "product" ? handleCreateProduct : handleCreateProductUsa}>
            <Plus className="mr-2 h-4 w-4" />
            {t("productsPage.add")}
          </Button>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-6">
        <div className="flex w-fit flex-row rounded-lg border border-gray-200 bg-white p-0.5">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`rounded-lg px-3 py-2 text-sm font-semibold focus:outline-hidden ${
                activeTab === tab
                  ? "bg-brand700 text-white"
                  : "bg-transparent text-gray-700 hover:text-gray-900"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tabLabels[tab]}
            </button>
          ))}
        </div>

        {activeTab === "product" ? (
          <>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <UnifiedInput
                inputType="text"
                leftElement={<Search />}
                placeholder={t("productsPage.searchPlaceholder")}
                value={productSearch}
                onChange={(value) => setProductSearch(String(value ?? ""))}
                wrapperClassName="md:max-w-xs"
                subWrapperClassName="h-10"
              />
              <SelectV2
                placeholder={t("productsPage.colorPlaceholder")}
                clearable
                options={wineColorOptions}
                value={productColor}
                onChange={(value) => setProductColor(value as WineColorEnum | "")}
                wrapperClassName="md:w-56"
                contentClassName="h-10"
              />
            </div>
            <DataTable
              data={products?.data || []}
              columns={productColumns}
              pagination={productPagination}
              setPagination={setProductPagination}
              sorting={productSorting}
              setSorting={setProductSorting}
              rowCount={products?.count || 0}
              autoResetPageIndex={false}
            />
          </>
        ) : (
          <>
            <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
              <UnifiedInput
                inputType="text"
                leftElement={<Search />}
                placeholder={t("productsPage.searchPlaceholder")}
                value={usaSearch}
                onChange={(value) => setUsaSearch(String(value ?? ""))}
                wrapperClassName="md:max-w-xs"
                subWrapperClassName="h-10"
              />
              <SelectV2
                placeholder={t("productsPage.colorPlaceholder")}
                clearable
                options={wineColorOptions}
                value={usaColor}
                onChange={(value) => setUsaColor(value as WineColorEnum | "")}
                wrapperClassName="md:w-52"
                contentClassName="h-10"
              />
              <SelectV2
                placeholder={t("productsPage.importerPlaceholder")}
                clearable
                searchable
                options={importerOptions}
                value={usaImporterId}
                onChange={(value) => setUsaImporterId(value)}
                wrapperClassName="md:w-52"
                contentClassName="h-10"
              />
              <SelectV2
                placeholder={t("productsPage.clientPlaceholder")}
                clearable
                searchable
                options={clientOptions}
                value={usaClientId}
                onChange={(value) => setUsaClientId(value)}
                wrapperClassName="md:w-52"
                contentClassName="h-10"
              />
            </div>
            <DataTable
              data={productsUsa?.data || []}
              columns={productUsaColumns}
              pagination={usaPagination}
              setPagination={setUsaPagination}
              sorting={usaSorting}
              setSorting={setUsaSorting}
              rowCount={productsUsa?.count || 0}
              autoResetPageIndex={false}
            />
          </>
        )}
      </div>

      {/* Product modals */}
      <ProductFormModal
        open={productFormOpen}
        onClose={handleCloseProductForm}
        product={editProduct}
      />

      {deleteProductModal && (
        <ProductDeleteModal
          open={!!deleteProductModal}
          onClose={handleCloseDeleteProduct}
          productName={deleteProductModal.description}
          onConfirm={() => deleteProduct(deleteProductModal.id)}
          isPending={isDeletingProduct}
        />
      )}

      {/* Product USA modals */}
      <ProductUsaFormModal
        open={productUsaFormOpen}
        onClose={handleCloseProductUsaForm}
        product={editProductUsa}
      />

      {deleteProductUsaModal && (
        <ProductDeleteModal
          open={!!deleteProductUsaModal}
          onClose={handleCloseDeleteProductUsa}
          productName={deleteProductUsaModal.description}
          onConfirm={() => deleteProductUsa(deleteProductUsaModal.id)}
          isPending={isDeletingProductUsa}
        />
      )}
    </div>
  );
};
